import { useState } from 'react';
import { trackBookingConfirmed } from './lib/track';
import './App.css';
import { Logo } from './components/Logo';
import { QualifyForm } from './components/QualifyForm';
import { Loading } from './components/Loading';
import { Matched } from './components/Matched';
import { SlotPicker } from './components/SlotPicker';
import { Booked } from './components/Booked';
import { Fallback } from './components/Fallback';
import { postBook, postQualify } from './lib/api/client';
import type {
  AvailabilitySlot,
  BookRequest,
  BookResponse,
  QualifyFallback,
  QualifyRequest,
  QualifySuccess,
} from './lib/api/types';

type BookSuccess = Extract<BookResponse, { ok: true }>;

/**
 * State machine for the booking widget. Two paths:
 *
 *   - advisor_first: form → submitting → matched → picking → booking → booked
 *     Used by Lead Owner (Rule 1) and Existing Client (Rule 1b) routes,
 *     where the prospect already has a relationship to a specific advisor.
 *
 *   - slot_first:    form → submitting → picking → booking → booked
 *     Used by tier and whole-team pool routes (Rules 2-8). The "matched"
 *     intermediate is skipped — the prospect doesn't know which advisor
 *     they'll be assigned until the post-booking reveal.
 */
type Phase =
  | { state: 'form' }
  | { state: 'submitting' }
  | { state: 'matched'; payload: QualifyRequest; result: QualifySuccess }
  | { state: 'picking'; payload: QualifyRequest; result: QualifySuccess }
  | {
      state: 'booking';
      payload: QualifyRequest;
      result: QualifySuccess;
      slot: AvailabilitySlot;
    }
  | { state: 'booked'; booking: BookSuccess; prospectEmail: string }
  | { state: 'fallback'; result: QualifyFallback };

function getProspectTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

function App() {
  const [phase, setPhase] = useState<Phase>({ state: 'form' });

  async function handleQualify(payload: QualifyRequest) {
    setPhase({ state: 'submitting' });
    try {
      const res = await postQualify(payload);
      if (!res.ok) {
        setPhase({ state: 'fallback', result: res });
        return;
      }
      // Slot-first: skip the "Matched" introduction screen — go straight
      // to picker since there is no specific advisor to introduce yet.
      // Advisor-first: show "Matched" so the prospect sees who they were
      // routed to before picking a time.
      if (res.kind === 'slot_first') {
        setPhase({ state: 'picking', payload, result: res });
      } else {
        setPhase({ state: 'matched', payload, result: res });
      }
    } catch {
      setPhase({
        state: 'fallback',
        result: {
          ok: false,
          fallback: true,
          reason: 'internal_error',
          message: 'Network error',
        },
      });
    }
  }

  function handleContinueToPicker() {
    if (phase.state !== 'matched') return;
    setPhase({ state: 'picking', payload: phase.payload, result: phase.result });
  }

  function handleBackFromPicker() {
    if (phase.state !== 'picking') return;
    // Advisor-first: bounce back to the matched screen.
    // Slot-first: bounce back to the form (there's no matched screen).
    if (phase.result.kind === 'advisor_first') {
      setPhase({ state: 'matched', payload: phase.payload, result: phase.result });
    } else {
      setPhase({ state: 'form' });
    }
  }

  async function handleSelectSlot(slot: AvailabilitySlot, tz: string) {
    if (phase.state !== 'picking') return;
    const { payload, result } = phase;
    setPhase({ state: 'booking', payload, result, slot });

    // Build the BookRequest based on which routing kind was returned.
    // Advisor-first sends a single advisorId. Slot-first sends the pool
    // slug + eligible advisor IDs; the server runs round-robin to pick
    // the specific advisor at INSERT time.
    const baseFields = {
      slotStartUtc: slot.startUtc,
      email: payload.email,
      phone: payload.phone,
      firstName: payload.firstName,
      lastName: payload.lastName,
      ruleHit: result.ruleHit,
      prospectTimezone: tz ?? getProspectTimezone(),
      utm_source: payload.utm_source,
      utm_medium: payload.utm_medium,
      utm_campaign: payload.utm_campaign,
      utm_content: payload.utm_content,
      utm_term: payload.utm_term,
      kuid: payload.kuid,
      kref: payload.kref,
    };
    const reqBody: BookRequest =
      result.kind === 'advisor_first'
        ? { ...baseFields, advisorId: result.advisor.id, poolSlug: null }
        : {
            ...baseFields,
            poolSlug: result.poolSlug,
            eligibleAdvisorIds: result.eligibleAdvisorIds,
          };

    try {
      const res = await postBook(reqBody);
      if (res.ok) {
        trackBookingConfirmed(res); // GTM dataLayer — vendor tags fire in Tag Manager
        setPhase({
          state: 'booked',
          booking: res,
          prospectEmail: payload.email,
        });
      } else if (res.error === 'slot_unavailable') {
        // Race lost — bounce back to picker so user can pick again
        setPhase({ state: 'picking', payload, result });
        // TODO: surface a toast "That slot was just taken"
      } else {
        setPhase({
          state: 'fallback',
          result: {
            ok: false,
            fallback: true,
            reason: 'internal_error',
            message: res.message,
          },
        });
      }
    } catch {
      setPhase({
        state: 'fallback',
        result: {
          ok: false,
          fallback: true,
          reason: 'internal_error',
          message: 'Network error during booking',
        },
      });
    }
  }

  function resetToForm() {
    setPhase({ state: 'form' });
  }

  return (
    <div className="app">
      <header className="header">
        <div className="logo-wrap">
          <Logo className="logo" />
          <p className="tagline">A better annuity buying experience</p>
        </div>
        <div className="accent-bar">
          <div className="accent-bar-blue" />
          <div className="accent-bar-red" />
        </div>
      </header>

      <main className="main">
        {phase.state === 'form' && (
          <>
            <h1>Schedule your 1st Appointment</h1>
            <p className="lead">
              A 60-minute Zoom call with one of our annuity specialists.
              Tell us a bit about yourself and we'll match you with the
              right advisor.
            </p>
            <div className="form-card">
              <QualifyForm onSubmit={handleQualify} submitting={false} />
            </div>
          </>
        )}

        {phase.state === 'submitting' && (
          <Loading message="Finding your advisor…" />
        )}

        {phase.state === 'matched' && phase.result.kind === 'advisor_first' && (
          <Matched match={phase.result} onContinue={handleContinueToPicker} />
        )}

        {phase.state === 'picking' && (
          <div className="form-card">
            <SlotPicker
              match={phase.result}
              onSelectSlot={handleSelectSlot}
              onBack={handleBackFromPicker}
            />
          </div>
        )}

        {phase.state === 'booking' && <Loading message="Confirming your time…" />}

        {phase.state === 'booked' && (
          <Booked booking={phase.booking} prospectEmail={phase.prospectEmail} />
        )}

        {phase.state === 'fallback' && (
          <Fallback fallback={phase.result} onRetry={resetToForm} />
        )}
      </main>

      <footer className="footer">
        <p>
          <strong>Annuity Association</strong> · 759 SW Federal Hwy, Stuart, FL
        </p>
        <p className="footer-small">
          © {new Date().getFullYear()} Annuity Association. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export default App;
